import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// The session fees come from the server (an admin sets them from the dashboard). The site never keeps its own copy of a fee:
// every price, GST amount and total it shows is what `GET /pricing` returned, i.e. exactly what checkout will charge.
const CACHE_MS = 60 * 1000;
let cached = null; // { data, at }
let inflight = null;

export const fetchPricing = async ({ force = false } = {}) => {
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.data;
  if (!force && inflight) return inflight;
  inflight = axios
    .get(`${process.env.REACT_APP_SERVER_URL}/pricing`)
    .then(({ data }) => {
      cached = { data: data.pricing, at: Date.now() };
      return cached.data;
    })
    .finally(() => { inflight = null; });
  return inflight;
};

// { pricing, loading, error, reload }. `pricing` is null until the fees have arrived (or if they could not be fetched):
// callers show a placeholder rather than a made-up number. `fresh` skips the one-minute cache (use it where money is decided).
export const usePricing = ({ fresh = false } = {}) => {
  const [pricing, setPricing] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(fresh || !cached);
  const [error, setError] = useState('');

  const load = useCallback(async (force) => {
    setLoading(true);
    try {
      setPricing(await fetchPricing({ force }));
      setError('');
    } catch (err) {
      setError('We could not load the session prices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(fresh); }, [load, fresh]);

  return { pricing, loading, error, reload: () => load(true) };
};

// The fee entry for the session length the customer picked. null while prices are not loaded.
export const priceFor = (pricing, { duration } = {}) => {
  if (!pricing) return null;
  return pricing.durations.find((entry) => entry.value === duration) || null;
};

export const shortestSession = (pricing) => (pricing && pricing.durations.length
  ? [...pricing.durations].sort((a, b) => a.minutes - b.minutes)[0]
  : null);

// "INR 999" for whole rupees, "INR 1178.82" otherwise.
export const formatInr = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'INR —';
  const number = Number(value);
  return `INR ${Number.isInteger(number) ? number : number.toFixed(2)}`;
};
